import { useFlags } from 'launchdarkly-react-client-sdk';

import * as FeatureFlagNames from '@/constants/featureFlags';
import ffDefaults from '@/constants/ffDefaults';
import type { FeatureFlags } from '@/constants/types';
import { Logger } from '@/utils/logger';
import { viteAppEnv, viteFFOverrides } from '@montugroup/circuit-shared/constants/env';

const logger = new Logger('useFeatureFlags');
const FF_OVERRIDE_LOCAL_STORAGE_KEY = 'FEATURE_FLAG_OVERRIDES';

const ENV_WITH_FF_OVERRIDES = ['local', 'test', 'development', 'qa', 'staging'];
const shouldApplyFFOverrides = ENV_WITH_FF_OVERRIDES.includes(viteAppEnv());

const getFFOverrides = (): Partial<FeatureFlags> => {
  let overrides = {};
  const localStorageFFOverrides = localStorage.getItem(FF_OVERRIDE_LOCAL_STORAGE_KEY);
  const envFFOverrides = viteFFOverrides();

  if (localStorageFFOverrides || envFFOverrides) {
    try {
      const parsedLocalStorageFFOverrides = localStorageFFOverrides ? JSON.parse(localStorageFFOverrides) : {};

      overrides = {
        ...(envFFOverrides ?? {}),
        ...parsedLocalStorageFFOverrides,
      };

      const overridenKeys = Object.entries(overrides).join(' | ');
      logger.info(`Overriding Feature Flags: ${overridenKeys}`);
    } catch {
      overrides = {};
    }
  }

  return overrides;
};

sessionStorage.setItem('FEATURE_FLAG_KEYS', JSON.stringify(Object.values(FeatureFlagNames), null, 2));
const featureFlagOverrides = getFFOverrides();

/**
 * Returns the fully merged feature flag object.
 *
 * @deprecated 🚫 Prefer `useFeatureFlag` or `useFeatureFlagsSelector` with a selector instead.
 *
 * This hook combines:
 * - default flag values (`ffDefaults`)
 * - remote flag values (`useFlags()`)
 * - optional runtime overrides (`featureFlagOverrides`)
 *
 * ⚠️ Subscription behavior:
 * This hook will rerender whenever *any* feature flag value changes,
 * because it subscribes to the entire flag set.
 *
 * ✅ Use this hook when:
 * - you need access to many or all flags
 * - building higher-level abstractions (e.g., selector hooks)
 * - performing one-time reads inside effects or event handlers
 *
 * 🚫 Avoid using this directly in leaf UI components that only depend
 * on a small number of flags, as it can cause unnecessary rerenders.
 *
 * @returns The full set of merged feature flags.
 */
const useFeatureFlags = (): { flags: FeatureFlags } => {
  const flags = {
    ...ffDefaults,
    ...useFlags<FeatureFlags>(),
  };

  if (shouldApplyFFOverrides) {
    if (featureFlagOverrides) {
      Object.keys(featureFlagOverrides).forEach((key) => {
        // NOTE: This should be `keyof typeof featureFlagOverrides` as seen on the previous line, just forcing TS
        const typedKey = key as keyof typeof featureFlagOverrides;

        flags[typedKey] = featureFlagOverrides[typedKey];
      });
    }
  }

  return {
    flags,
  };
};

export default useFeatureFlags;
